import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  TextField,
  Collapse,
  TableContainer,
  TablePagination,
  Checkbox,
  Chip,
  Alert,
  Snackbar,
  Stack,
  Tooltip,
  Tabs,
  Tab
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import ClearIcon from "@mui/icons-material/Clear";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ShowChartIcon from "@mui/icons-material/ShowChart";

import ConfirmDialog from "../../components/ConfirmDialog";
import ImportDialog from "../../components/ImportDialog";
import AddEditMeasurementDialog from "./AddEditMeasurementDialog";
import MeasurementDetailsDialog from "../../components/MeasurementDetailsDialog";
import CumulativeTrendPage from "./CumulativeTrendPage";
import { useTheme, useMediaQuery } from "@mui/material";
import {
  createMeasurement,
  updateMeasurement,
  deleteMeasurement,
  deleteBulkMeasurements,
  getMeasurementsByStudent,
  getAllMeasurements
} from "../../services/physicalMeasurementsApi";
import {
  createStudent,
  updateStudent
} from "../../services/studentApi";

import ExpandRowDetails from "./ExpandRowDetails";
import { exportExcel } from "../../utils/excelUtils";
import { calculateBodyMetrics, parseFlexibleDate } from "../../utils/bodyMetrics";
import StudentTrendChartDialog from "../../components/StudentTrendChartDialog";

export const normalizeStudentData = (student: any = {}, row: any = {}) => {
  let name = student.name ?? student.fullName ?? student.full_name ?? row.name ?? row.fullName ?? row.full_name ?? "-";
  let email = student.email ?? row.email ?? "";
  let mobile = student.mobile ?? student.phone ?? student.mobileNumber ?? student.mobile_number ?? row.mobile ?? row.phone ?? "";
  let gender = student.gender ?? row.gender ?? "";
  let dob = student.dob ?? student.dateOfBirth ?? student.date_of_birth ?? row.dob ?? row.dateOfBirth ?? row.date_of_birth ?? "";

  const isGenderStr = (val: any) => typeof val === "string" && ["male", "female", "other"].includes(val.trim().toLowerCase());
  const isDateStr = (val: any) => {
    if (!val || typeof val !== "string") return false;
    const trimmed = val.trim();
    return /^\d{4}-\d{2}-\d{2}/.test(trimmed) || /^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4}/.test(trimmed) || (!isNaN(Date.parse(trimmed)) && trimmed.includes("-"));
  };
  const isNumericStr = (val: any) => {
    if (val === null || val === undefined || val === "") return false;
    return !isNaN(Number(val));
  };

  // 1. Shifted column recovery: Email contains gender
  if (isGenderStr(email) && (!gender || isNumericStr(gender) || gender === "-")) {
    gender = email.trim();
    email = "";
  }

  // 2. Shifted column recovery: Mobile contains date / DOB
  if (isDateStr(mobile) && (!dob || !isDateStr(dob))) {
    dob = mobile.trim();
    mobile = "";
  }

  // 3. Gender holds numeric (e.g. age 8 or index)
  if (isNumericStr(gender) && !isGenderStr(gender)) {
    gender = "";
  }

  // Capitalize gender or fallback to "-"
  if (isGenderStr(gender)) {
    gender = gender.trim().charAt(0).toUpperCase() + gender.trim().slice(1).toLowerCase();
  } else {
    gender = "-";
  }

  if (!email || email === "") email = "-";
  if (!mobile || mobile === "") mobile = "-";
  if (!dob || dob === "") dob = "-";

  return { name, email, mobile, gender, dob };
};

export const parseMeasurementTimestamp = (val?: string | Date | number | any[] | null): Date | null => {
  if (!val || val === "-" || val === "N/A") return null;
  if (val instanceof Date) {
    return isNaN(val.getTime()) ? null : val;
  }
  if (typeof val === "number") {
    if (val > 20000 && val < 75000) {
      return new Date(Math.round((val - 25569) * 86400 * 1000));
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  if (Array.isArray(val) && val.length >= 3) {
    const year = val[0];
    const month = val[1] - 1;
    const day = val[2];
    const hour = val[3] || 0;
    const min = val[4] || 0;
    const sec = val[5] || 0;
    const d = new Date(year, month, day, hour, min, sec);
    return isNaN(d.getTime()) ? null : d;
  }

  const str = String(val).trim();
  if (!str) return null;

  // 1. ISO / SQL datetime with time: YYYY-MM-DD[T or space]HH:mm(:ss)?
  const isoTimeMatch = str.match(/^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2})(?:\.(\d+))?)?(Z|[+-]\d{2}(?::?\d{2})?)?$/i);
  if (isoTimeMatch) {
    const tz = isoTimeMatch[8];
    if (tz) {
      const d = new Date(str);
      if (!isNaN(d.getTime())) return d;
    }
    const year = parseInt(isoTimeMatch[1], 10);
    const month = parseInt(isoTimeMatch[2], 10) - 1;
    const day = parseInt(isoTimeMatch[3], 10);
    const hours = parseInt(isoTimeMatch[4], 10);
    const minutes = parseInt(isoTimeMatch[5], 10);
    const seconds = isoTimeMatch[6] ? parseInt(isoTimeMatch[6], 10) : 0;
    const millis = isoTimeMatch[7] ? parseInt(isoTimeMatch[7].substring(0, 3).padEnd(3, "0"), 10) : 0;
    const d = new Date(year, month, day, hours, minutes, seconds, millis);
    if (!isNaN(d.getTime())) return d;
  }

  // 2. DD/MM/YYYY or DD-MM-YYYY with time (optional AM/PM): DD/MM/YYYY HH:mm(:ss)? (AM/PM)?
  const dmyTimeMatch = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?(?:\s*(AM|PM))?/i);
  if (dmyTimeMatch) {
    const day = parseInt(dmyTimeMatch[1], 10);
    const month = parseInt(dmyTimeMatch[2], 10) - 1;
    const year = parseInt(dmyTimeMatch[3], 10);
    let hours = parseInt(dmyTimeMatch[4], 10);
    const minutes = parseInt(dmyTimeMatch[5], 10);
    const seconds = dmyTimeMatch[6] ? parseInt(dmyTimeMatch[6], 10) : 0;
    const ampm = dmyTimeMatch[7] ? dmyTimeMatch[7].toUpperCase() : null;
    if (ampm === "PM" && hours < 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;
    const d = new Date(year, month, day, hours, minutes, seconds);
    if (!isNaN(d.getTime())) return d;
  }

  // 3. Date only: YYYY-MM-DD or YYYY/MM/DD
  const ymdMatch = str.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
  if (ymdMatch) {
    const year = parseInt(ymdMatch[1], 10);
    const month = parseInt(ymdMatch[2], 10) - 1;
    const day = parseInt(ymdMatch[3], 10);
    const d = new Date(year, month, day);
    if (!isNaN(d.getTime())) return d;
  }

  // 4. Date only: DD/MM/YYYY or DD-MM-YYYY
  const dmyMatch = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (dmyMatch) {
    const day = parseInt(dmyMatch[1], 10);
    const month = parseInt(dmyMatch[2], 10) - 1;
    const year = parseInt(dmyMatch[3], 10);
    const d = new Date(year, month, day);
    if (!isNaN(d.getTime())) return d;
  }

  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

export const formatMeasurementDateTime = (dateVal?: any): string => {
  if (!dateVal || dateVal === "-" || dateVal === "N/A") return "-";
  const d = parseMeasurementTimestamp(dateVal);
  if (!d) return "-";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  let hours = d.getHours();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  const displayHours = String(hours ? hours : 12).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${displayHours}:${minutes} ${ampm}`;
};

export const formatDisplayDate = (dateVal?: any): string => {
  if (!dateVal || dateVal === "-" || dateVal === "N/A") return "-";
  const d = parseMeasurementTimestamp(dateVal);
  if (!d) return "-";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const PhysicalMeasurementsList = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [addEditOpen, setAddEditOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [dateTime, setDateTime] = useState(new Date());
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [totalElements, setTotalElements] = React.useState(0);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);

  const [activeTab, setActiveTab] = useState<"records" | "cumulative">("records");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({
    open: false,
    message: "",
    severity: "success"
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));

  const [trendOpen, setTrendOpen] = useState(false);
  const [trendRow, setTrendRow] = useState<any>(null);
  const [trendMeasurements, setTrendMeasurements] = useState<any[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const formatDate = (dateVal?: any) => formatDisplayDate(dateVal);
  const formatDateTime = (dateVal?: any) => formatMeasurementDateTime(dateVal);

  useEffect(() => {
    const timer = setInterval(() => setDateTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadData();
  }, [selectedStudentId, page, rowsPerPage, search]);

  const loadData = async () => {
    try {
      const res = selectedStudentId
        ? await getMeasurementsByStudent(selectedStudentId, page, rowsPerPage, search)
        : await getAllMeasurements(page, rowsPerPage, search);

      const data = res.data?.content ?? (Array.isArray(res.data) ? res.data : []);

      const enrichedData = data.map((row: any) => {
        const student = row.student ?? {};
        const normalized = normalizeStudentData(student, row);

        const lastMeasurementDate =
          row.lastMeasurement ||
          row.measurementDate ||
          row.measurement_date ||
          row.createdAt ||
          row.created_at ||
          "";

        const metrics = calculateBodyMetrics(
          Number(row.height || 0),
          Number(row.weight || 0),
          normalized.dob !== "-" ? normalized.dob : "",
          normalized.gender !== "-" ? (normalized.gender as "Male" | "Female") : "Male",
          Number(row.heartRate || row.heart_rate || 70),
          lastMeasurementDate
        );

        return {
          ...row,
          studentId: student.id ?? row.studentId ?? row.student_id,
          name: normalized.name,
          email: normalized.email,
          mobile: normalized.mobile,
          gender: normalized.gender,
          dob: normalized.dob,
          lastMeasurement: lastMeasurementDate,
          height: row.height ?? "-",
          weight: row.weight ?? "-",
          heartRate: row.heartRate ?? row.heart_rate ?? "-",
          ...metrics
        };
      });

      setRows(enrichedData);
      setTotalElements(res.data?.totalElements ?? enrichedData.length);
    } catch (err) {
      console.error("Failed to load measurements data:", err);
    }
  };

  const handleSave = async (data: any) => {
    try {
      if (data.mode === "CREATE_STUDENT") {
        const studentRes = await createStudent({
          name: data.name,
          email: data.email,
          mobile: data.mobile,
          gender: data.gender,
          dob: data.dob
        });

        await createMeasurement(studentRes.data.id, {
          height: Number(data.height),
          weight: Number(data.weight),
          heartRate: Number(data.heartRate),
          lastMeasurement: data.lastMeasurement || data.measurementDate
        });
      } else if (data.mode === "EDIT") {
        if (data.studentId) {
          await updateStudent(data.studentId, {
            name: data.name,
            email: data.email,
            mobile: data.mobile,
            gender: data.gender,
            dob: data.dob
          });
        }

        await updateMeasurement(data.id, {
          height: Number(data.height),
          weight: Number(data.weight),
          heartRate: Number(data.heartRate)
        });
      } else if (data.mode === "ADD_MEASUREMENT") {
        await createMeasurement(data.studentId, {
          height: Number(data.height),
          weight: Number(data.weight),
          heartRate: Number(data.heartRate),
          lastMeasurement: data.lastMeasurement || data.measurementDate
        });
      }

      setAddEditOpen(false);
      setEditData(null);
      setSnackbar({
        open: true,
        message: "Measurement saved successfully!",
        severity: "success"
      });
      loadData();
    } catch (err) {
      console.error("Save failed", err);
      setSnackbar({
        open: true,
        message: "Failed to save measurement. Please check your inputs.",
        severity: "error"
      });
    }
  };

  // Single Record Delete
  const handleDelete = async () => {
    if (deleteId) {
      try {
        await deleteMeasurement(deleteId);
        setDeleteConfirmOpen(false);
        setDeleteId(null);
        setSelectedIds(prev => prev.filter(id => id !== deleteId));
        setSnackbar({
          open: true,
          message: "Measurement record deleted successfully!",
          severity: "success"
        });
        loadData();
      } catch (err) {
        console.error("Delete failed", err);
        setSnackbar({
          open: true,
          message: "Failed to delete record.",
          severity: "error"
        });
      }
    }
  };

  // Admin Bulk Delete Handler
  const handleBulkDeleteConfirm = async () => {
    if (selectedIds.length === 0) return;

    try {
      const response = await deleteBulkMeasurements(selectedIds);
      const count = response.data?.deletedCount ?? selectedIds.length;
      setBulkDeleteConfirmOpen(false);
      setSelectedIds([]);
      setSnackbar({
        open: true,
        message: `Successfully deleted ${count} selected record(s).`,
        severity: "success"
      });
      loadData();
    } catch (err: any) {
      console.error("Bulk delete failed:", err);
      setSnackbar({
        open: true,
        message: "Failed to delete selected records. Please try again.",
        severity: "error"
      });
    }
  };

  const filteredRows = rows.filter((r) => {
    const text = `
    ${r.name ?? ""}
    ${r.email ?? ""}
    ${r.mobile ?? ""}
  `.toLowerCase();

    return text.includes(search.toLowerCase());
  });

  // Selection helpers
  const visibleRowIds = filteredRows.map(r => r.id).filter(Boolean);
  const isAllVisibleSelected = visibleRowIds.length > 0 && visibleRowIds.every(id => selectedIds.includes(id));
  const isIndeterminate = visibleRowIds.some(id => selectedIds.includes(id)) && !isAllVisibleSelected;

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const newSet = new Set([...selectedIds, ...visibleRowIds]);
      setSelectedIds(Array.from(newSet));
    } else {
      setSelectedIds(selectedIds.filter(id => !visibleRowIds.includes(id)));
    }
  };

  const handleToggleSelectRow = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* ===== Header AppBar ===== */}
      <AppBar
        position="sticky"
        elevation={1}
        sx={{
          backgroundColor: "#ffffff",
          color: "#000000"
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 1, sm: 0 }
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src="/logo.jpg"
            alt="Logo"
            sx={{
              height: 40,
              width: "auto"
            }}
          />

          {/* Title */}
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              color: "#1F497D",
              ml: 1
            }}
          >
            Physical Measurement
          </Typography>

          {/* Right Section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ fontWeight: 500 }}>
              Hello, Admin
            </Typography>

            <Box
              component="img"
              src="/wavehand.jpg"
              alt="hand"
              sx={{ height: 36 }}
            />

            {!isCompact && (
              <Typography variant="body2" color="text.secondary">
                {dateTime.toLocaleDateString("en-GB")}{" "}
                {dateTime.toLocaleTimeString()}
              </Typography>
            )}

            {!isCompact && (
              <Button
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: "#1F497D",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#163A63"
                  }
                }}
              >
                Logout
              </Button>
            )}
          </Box>
        </Toolbar>

        {/* View Mode Navigation Tabs */}
        <Box sx={{ borderTop: "1px solid #E4E4F2", px: 2, backgroundColor: "#ffffff" }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 44,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                minHeight: 44,
                py: 0.5,
                color: "#555"
              },
              "& .Mui-selected": {
                color: "#1F497D !important"
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#1F497D",
                height: 3
              }
            }}
          >
            <Tab
              icon={<FormatListBulletedIcon fontSize="small" />}
              iconPosition="start"
              label="Measurement Records"
              value="records"
            />
            <Tab
              icon={<ShowChartIcon fontSize="small" />}
              iconPosition="start"
              label="Cumulative Population Trends"
              value="cumulative"
            />
          </Tabs>
        </Box>
      </AppBar>

      {/* ===== Main Content Area ===== */}
      {activeTab === "cumulative" ? (
        <CumulativeTrendPage />
      ) : (
        <>
          {/* ===== Second Row: Actions & Search ===== */}
      <Box
        sx={{
          backgroundColor: "#F6F6FF",
          borderTop: "1px solid #E4E4F2",
          p: 2,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        {/* Left Actions: Add Button */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditData({
                mode: "CREATE_STUDENT"
              });
              setAddEditOpen(true);
            }}
            sx={{
              backgroundColor: "#1F497D",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#163A63"
              }
            }}
          >
            Add Student
          </Button>

          {/* Admin Bulk Delete Button (Visible when rows are selected) */}
          {selectedIds.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={() => setBulkDeleteConfirmOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                boxShadow: 2
              }}
            >
              Delete ({selectedIds.length}) Selected
            </Button>
          )}
        </Stack>

        {/* Search Box */}
        <TextField
          size="small"
          placeholder="Search by student name, email, or mobile..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            flexGrow: 1,
            maxWidth: { xs: "100%", md: 550 },
            backgroundColor: "#ffffff",
            borderRadius: 1,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#E4E4F2"
              },
              "&:hover fieldset": {
                borderColor: "#1F497D"
              },
              "&.Mui-focused fieldset": {
                borderColor: "#1F497D"
              }
            }
          }}
        />

        {/* Import / Export & Selection Toolbar */}
        <Stack direction="row" spacing={1} alignItems="center">
          {selectedIds.length > 0 && (
            <Chip
              label={`${selectedIds.length} selected`}
              color="primary"
              variant="outlined"
              size="small"
              onDelete={() => setSelectedIds([])}
              deleteIcon={<ClearIcon />}
              sx={{ borderColor: "#1F497D", color: "#1F497D", fontWeight: 600 }}
            />
          )}

          <Button
            variant="contained"
            startIcon={<FileUploadIcon />}
            sx={{
              backgroundColor: "#1F497D",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#163A63"
              }
            }}
            onClick={() => setImportOpen(true)}
          >
            Import
          </Button>

          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            sx={{
              backgroundColor: "#1F497D",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#163A63"
              }
            }}
            onClick={() => exportExcel(filteredRows)}
          >
            Export
          </Button>
        </Stack>
      </Box>

      {/* ===== Scrollable Table ===== */}
      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        <TableContainer
          component={Paper}
          sx={{
            minWidth: 1200,
            overflowX: "auto",
            boxShadow: 2,
            borderRadius: 1.5
          }}
        >
          <Table size="small">
            <TableHead sx={{ backgroundColor: "#d9e4f5" }}>
              <TableRow
                sx={{
                  "& th": {
                    backgroundColor: "#1F497D",
                    color: "#FFFFFF",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    borderBottom: "none"
                  },
                  "& th:first-of-type": {
                    borderTopLeftRadius: "8px"
                  },
                  "& th:last-of-type": {
                    borderTopRightRadius: "8px"
                  }
                }}
              >
                {/* Admin Select All Checkbox */}
                <TableCell padding="checkbox" sx={{ pl: 1 }}>
                  <Tooltip title={isAllVisibleSelected ? "Deselect All Visible" : "Select All Visible"}>
                    <Checkbox
                      indeterminate={isIndeterminate}
                      checked={isAllVisibleSelected}
                      onChange={handleSelectAll}
                      size="small"
                      sx={{
                        color: "rgba(255,255,255,0.8)",
                        "&.Mui-checked": { color: "#FFFFFF" },
                        "&.MuiCheckbox-indeterminate": { color: "#FFFFFF" }
                      }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>S.no</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>DOB</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Last Measurement</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Height (cm)</TableCell>
                <TableCell>Weight (kg)</TableCell>
                <TableCell>Heart Rate</TableCell>
                <TableCell>BMI</TableCell>
                <TableCell>Body Fat (%)</TableCell>
                <TableCell>Muscle Mass</TableCell>
                <TableCell>Bone Mass</TableCell>
                <TableCell>
                  <Tooltip title="Pediatric BMR estimated via Schofield / FAO-WHO-UNU methodology">
                    <span>BMR</span>
                  </Tooltip>
                </TableCell>
                <TableCell>Protein</TableCell>
                <TableCell>Body Moisture</TableCell>
                <TableCell>
                  <Tooltip title="Non-clinical proxy rating estimated from WHO BMI Z-score">
                    <span>Visceral Index (Estimated)</span>
                  </Tooltip>
                </TableCell>
                <TableCell>Subcutaneous Fat</TableCell>
                <TableCell>Lean Body Mass</TableCell>
                <TableCell>Body Type</TableCell>
                <TableCell>
                  <Tooltip title="WHO pediatric BMI-for-age Z-score classification (Ages 5–19)">
                    <span>WHO BMI-for-age</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="Reference median weight based on pediatric WHO reference methodology.">
                    <span>Reference Median Weight (P50)</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={26} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    <Typography variant="body1">No measurement records found.</Typography>
                    <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                      Click "Add Student" or "Import" to record physical measurements.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row, index) => {
                  const isSelected = selectedIds.includes(row.id);
                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        hover
                        selected={isSelected}
                        sx={{
                          backgroundColor: isSelected
                            ? "#D6E4FF !important"
                            : index % 2 === 0 ? "#EEF0FF" : "#CBD4E5",
                          "&:hover": {
                            backgroundColor: isSelected ? "#C8DBFF !important" : "#D6DBF5"
                          }
                        }}
                      >
                        {/* Row Checkbox */}
                        <TableCell padding="checkbox" sx={{ pl: 1 }}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleToggleSelectRow(row.id)}
                            size="small"
                            sx={{
                              color: "#1F497D",
                              "&.Mui-checked": { color: "#1F497D" }
                            }}
                          />
                        </TableCell>

                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            width: 80
                          }}
                        >
                          {/* Trend Icon */}
                          <Tooltip title="View Student Growth Trends & WHO Curves">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={async () => {
                                setTrendRow(row);

                                try {
                                  let rawData: any[] = [];
                                  if (row.studentId) {
                                    const res = await getMeasurementsByStudent(row.studentId, 0, 1000, "");
                                    rawData = res.data?.content ?? (Array.isArray(res.data) ? res.data : []);
                                  }

                                  if (!rawData || rawData.length === 0) {
                                    const res = await getAllMeasurements(0, 1000, row.name !== "-" ? row.name : "");
                                    rawData = res.data?.content ?? (Array.isArray(res.data) ? res.data : []);
                                  }

                                  const targetStudentId = row.studentId;
                                  const targetName = (row.name || "").trim().toLowerCase();

                                  const studentHistory = rawData.filter((m: any) => {
                                    const mStudentId = m.student?.id ?? m.studentId ?? m.student_id;
                                    const mName = (m.student?.name ?? m.name ?? "").trim().toLowerCase();

                                    if (targetStudentId && mStudentId) {
                                      return String(mStudentId) === String(targetStudentId);
                                    }
                                    if (targetName && targetName !== "-") {
                                      return mName === targetName;
                                    }
                                    return false;
                                  });

                                  const finalHistory = studentHistory.length > 0 ? studentHistory : [row];

                                  finalHistory.sort((a: any, b: any) => {
                                     const dateValA = a.lastMeasurement || a.last_measurement || a.measurementDate || a.measurement_date || a.measuredAt || a.measured_at || a.date || a.createdAt || a.created_at || 0;
                                     const dateValB = b.lastMeasurement || b.last_measurement || b.measurementDate || b.measurement_date || b.measuredAt || b.measured_at || b.date || b.createdAt || b.created_at || 0;
                                     const timeA = parseMeasurementTimestamp(dateValA)?.getTime() ?? 0;
                                     const timeB = parseMeasurementTimestamp(dateValB)?.getTime() ?? 0;
                                     return timeA - timeB;
                                  });

                                  setTrendMeasurements(finalHistory);
                                } catch (err) {
                                  console.error("Failed to load student trend measurements:", err);
                                  setTrendMeasurements([row]);
                                }
                                setTrendOpen(true);
                              }}
                            >
                              <TrendingUpIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* Expand / Collapse Icon */}
                          <Tooltip title={expandedId === row.id ? "Collapse Details" : "Expand Details"}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                setExpandedId(expandedId === row.id ? null : row.id)
                              }
                            >
                              {expandedId === row.id ? <RemoveIcon /> : <AddIcon />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>

                        <TableCell sx={{ fontWeight: 600, color: "#1F497D" }}>{row.name}</TableCell>
                        <TableCell>{row.gender}</TableCell>
                        <TableCell>{formatDate(row.dob)}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.mobile}</TableCell>
                        <TableCell>{formatDateTime(row.lastMeasurement)}</TableCell>
                        <TableCell>{row.exactAgeYears > 0 ? `${row.exactAgeYears} years` : (row.age && row.age !== "-" ? `${row.age} years` : "-")}</TableCell>
                        <TableCell>{row.height}</TableCell>
                        <TableCell>{row.weight}</TableCell>
                        <TableCell>{row.heartRate}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{row.bmi}</TableCell>
                        <TableCell>{row.bodyFat}</TableCell>
                        <TableCell>{row.muscleMass}</TableCell>
                        <TableCell>{row.boneMass}</TableCell>
                        <TableCell>{row.bmr}</TableCell>
                        <TableCell>{row.protein}</TableCell>
                        <TableCell>{row.bodyMoisture}</TableCell>
                        <TableCell>{row.visceralFat}</TableCell>
                        <TableCell>{row.subcutaneousFat}</TableCell>
                        <TableCell>{row.leanBodyMass}</TableCell>
                        <TableCell>{row.bodyType}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#1F497D" }}>{row.obesityLevel}</TableCell>
                        <TableCell>{row.standardWeight}</TableCell>
                      </TableRow>

                      {/* Expandable Accordion Row */}
                      <TableRow>
                        <TableCell colSpan={26} sx={{ p: 0 }}>
                          <Collapse in={expandedId === row.id}>
                            <Box p={2} bgcolor="#FAFAFA" sx={{ borderBottom: "1px solid #E0E0E0" }}>
                              <ExpandRowDetails data={row} />
                              <Box mt={1.5} display="flex" gap={1} flexWrap="wrap">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setSelectedRow(row);
                                    setDetailsOpen(true);
                                  }}
                                  sx={{ borderColor: "#1F497D", color: "#1F497D" }}
                                >
                                  View Details
                                </Button>

                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setEditData({
                                      mode: "EDIT",
                                      id: row.id,
                                      studentId: row.studentId,
                                      name: row.name !== "-" ? row.name : "",
                                      email: row.email !== "-" ? row.email : "",
                                      mobile: row.mobile !== "-" ? row.mobile : "",
                                      gender: row.gender !== "-" ? row.gender : "Male",
                                      dob: row.dob !== "-" ? row.dob : "",
                                      height: row.height !== "-" ? row.height : "",
                                      weight: row.weight !== "-" ? row.weight : "",
                                      heartRate: row.heartRate !== "-" ? row.heartRate : ""
                                    });

                                    setAddEditOpen(true);
                                  }}
                                  sx={{ borderColor: "#1F497D", color: "#1F497D" }}
                                >
                                  Edit Measurement
                                </Button>

                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setEditData({
                                      mode: "ADD_MEASUREMENT",
                                      studentId: row.studentId,
                                      name: row.name !== "-" ? row.name : "",
                                      email: row.email !== "-" ? row.email : "",
                                      mobile: row.mobile !== "-" ? row.mobile : "",
                                      gender: row.gender !== "-" ? row.gender : "Male",
                                      dob: row.dob !== "-" ? row.dob : ""
                                    });

                                    setAddEditOpen(true);
                                  }}
                                  sx={{ borderColor: "#1F497D", color: "#1F497D" }}
                                >
                                  Add New Measurement
                                </Button>

                                <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  onClick={() => {
                                    setDeleteId(row.id);
                                    setDeleteConfirmOpen(true);
                                  }}
                                >
                                  Delete Record
                                </Button>
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={(_event, newPage) => {
            setPage(newPage);
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          sx={{
            borderTop: "1px solid #E4E4F2",
            backgroundColor: "#F6F6FF"
          }}
        />
      </Box>
      </>
      )}

      {/* ===== Dialogs & Feedback ===== */}

      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={loadData}
      />

      <AddEditMeasurementDialog
        open={addEditOpen}
        editData={editData}
        onClose={() => {
          setAddEditOpen(false);
          setEditData(null);
        }}
        onSave={handleSave}
      />

      <MeasurementDetailsDialog
        open={detailsOpen}
        row={selectedRow}
        onClose={() => setDetailsOpen(false)}
      />

      {/* Single Delete Confirmation */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Confirm Record Deletion"
        message="Are you sure you want to delete this measurement record? This action cannot be undone."
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDeleteId(null);
        }}
        onConfirm={handleDelete}
      />

      {/* Admin Bulk Delete Confirmation */}
      <ConfirmDialog
        open={bulkDeleteConfirmOpen}
        title="Confirm Admin Bulk Deletion"
        message={`Are you sure you want to delete ${selectedIds.length} selected measurement record(s)? This action cannot be undone.`}
        onClose={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={handleBulkDeleteConfirm}
      />

      {/* Student Trend Chart Dialog */}
      <StudentTrendChartDialog
        open={trendOpen}
        onClose={() => setTrendOpen(false)}
        row={trendRow}
        measurements={trendMeasurements.map((m: any) => {
          const rawDate =
            m.measurementDate ||
            m.measurement_date ||
            m.lastMeasurement ||
            m.last_measurement ||
            m.measuredAt ||
            m.measured_at ||
            m.date ||
            m.createdAt ||
            m.created_at ||
            "";

          return {
            ...m,
            date: rawDate ? (parseMeasurementTimestamp(rawDate)?.toISOString() || String(rawDate)) : new Date().toISOString(),
            height: Number(m.height || 0),
            weight: Number(m.weight || 0),
            heartRate: Number(m.heartRate || m.heart_rate || 70),
            bmi: Number(m.bmi || 0),
            bodyFat: Number(m.bodyFat || m.body_fat || 0),
            muscleMass: Number(m.muscleMass || m.muscle_mass || 0),
            boneMass: Number(m.boneMass || m.bone_mass || 0),
            bmr: Number(m.bmr || 0),
            protein: Number(m.protein || 0),
            bodyMoisture: Number(m.bodyMoisture || m.body_moisture || 0),
            visceralFat: Number(m.visceralFat || m.visceral_fat || 0),
            subcutaneousFat: Number(m.subcutaneousFat || m.subcutaneous_fat || 0),
            leanBodyMass: Number(m.leanBodyMass || m.lean_body_mass || 0),
            standardWeight: Number(m.standardWeight || m.standard_weight || 0),
            name: m.student?.name || m.name || trendRow?.name,
            studentId: m.student?.id || m.studentId || m.student_id || trendRow?.studentId
          };
        })}
      />

      {/* Toast Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%", boxShadow: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PhysicalMeasurementsList;
